import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Building2, Check, CheckCircle2, Copy, Phone, PhoneCall } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useTheme } from '../contexts/ThemeContext';
import { useScreenInit } from '../useScreenInit.js';
interface PaymentConfirmation {
  planName: string;
  isTrial: boolean;
  trialEndsLabel: string;
}
interface ForwardingSetupScreenProps {
  onBack: () => void;
  /** Present only when arriving straight from a successful checkout. */
  paymentConfirmation?: PaymentConfirmation;
  onChooseMobile: () => void;
  onLandlineActivated: () => void;
}
const VEXU_NUMBER = '+1 (555) 019-8472';
export function ForwardingSetupScreen({
  onBack,
  paymentConfirmation,
  onChooseMobile,
  onLandlineActivated
}: ForwardingSetupScreenProps) {
  const {
    isLight
  } = useTheme();
  const screenInit = useScreenInit();
  const [selection, setSelection] = useState<'mobile' | 'landline' | null>(screenInit.selection === 'landline' || screenInit.selection === 'mobile' ? screenInit.selection : null);
  const [copied, setCopied] = useState(false);
  const copyForwardingNumber = () => {
    navigator.clipboard.writeText(VEXU_NUMBER);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };
  const isLandline = selection === 'landline';
  return <div className={`h-full flex flex-col overflow-hidden ${isLight ? 'bg-stone-100' : 'bg-black'}`} data-id="element-2318" data-mp="2e2dlklkv1okc">
      <header className="px-6 pt-6 pb-4 flex items-center gap-3" data-id="element-2319" data-mp="2i2ea4z1obg54">
        <button onClick={onBack} aria-label="Back" className={`p-2 -ml-2 rounded-full transition-colors ${isLight ? 'bg-white text-slate-500 border border-slate-200 hover:text-slate-900' : 'bg-white/5 text-zinc-400 border border-white/10 hover:text-white'}`} data-id="element-2320" data-mp="2n2iq8nyo68ut">
          <ArrowLeft className="w-5 h-5" data-id="element-2321" data-mp="xncbr9gqjigy" />
        </button>
        <div data-id="element-2322" data-mp="xnugyvldl710">
          <p className={`text-[11px] font-bold uppercase tracking-wider ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2323" data-mp="xpotblikzqth">
            One last step
          </p>
          <h1 className={`text-lg font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2324" data-mp="lmvy2ofi9wcu">
            Connect a number
          </h1>
        </div>
      </header>

      <div className="flex-1 overflow-y-auto px-6 pb-8" data-id="element-2325" data-mp="ul7wvdqj00gd">
        <AnimatePresence mode="wait" data-id="element-2326" data-mp="18lioiizntj2l">
          {!isLandline ? <motion.div key="number-choice" initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -12
        }} transition={{
          duration: 0.22
        }} className="pt-7" data-id="element-2327" data-mp="18jnyiq9j70rn">
              {paymentConfirmation && <motion.div role="status" initial={{
            opacity: 0,
            y: -6,
            scale: 0.98
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.24,
            ease: [0.23, 1, 0.32, 1]
          }} className={`-mt-3 mb-7 flex items-center gap-3 rounded-2xl border p-3.5 ${isLight ? 'border-vexu/25 bg-vexu/[0.06]' : 'border-vexu/25 bg-vexu/10'}`} data-id="element-2328" data-mp="1n8k6yofi2fss">
                  <motion.span initial={{
              scale: 0.6,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.22,
              delay: 0.12,
              ease: [0.23, 1, 0.32, 1]
            }} className="grid h-8 w-8 flex-shrink-0 place-items-center rounded-full bg-vexu text-white" aria-hidden data-id="element-2329" data-mp="1n7xw2rpb3f0n">
                    <Check className="h-4 w-4" strokeWidth={3} data-id="element-2330" data-mp="1493frea726id" />
                  </motion.span>
                  <div className="min-w-0" data-id="element-2331" data-mp="1997twxnmqp5f">
                    <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2332" data-mp="197ws33dqvrgy">
                      {paymentConfirmation.isTrial ? 'Your free trial has started' : 'Payment confirmed'}
                    </p>
                    <p className={`mt-0.5 text-xs leading-relaxed ${isLight ? 'text-slate-600' : 'text-zinc-400'}`} data-id="element-2333" data-mp="22tsqvmc81u3h">
                      {paymentConfirmation.isTrial ? `Vexu ${paymentConfirmation.planName} · free until ${paymentConfirmation.trialEndsLabel}` : `Vexu ${paymentConfirmation.planName} is now active`}
                    </p>
                  </div>
                </motion.div>}
              <div className="mb-8" data-id="element-2334" data-mp="1jvpw0qwzt2xr">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2335" data-mp="1jwrgtn3t93so">
                  <PhoneCall className="w-7 h-7" data-id="element-2336" data-mp="3e6ldkiq5nsco" />
                </div>
                <h2 className={`text-2xl font-bold tracking-tight mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2337" data-mp="39ae6gmm0wat5">
                  Which number should Vexu answer?
                </h2>
                <p className={`text-sm leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2338" data-mp="3lq45jehdmvxe">
                  Choose the number you want to forward to your AI assistant.
                  We’ll show the right setup instructions next.
                </p>
              </div>

              <div className="space-y-3" data-id="element-2339" data-mp="32tcrpkklt90q">
                <button onClick={() => setSelection('mobile')} className={`w-full p-4 rounded-2xl text-left border transition-colors ${isLight ? 'bg-white border-slate-200 hover:border-vexu/50 hover:bg-vexu/[0.02] shadow-sm' : 'bg-white/[0.04] border-white/10 hover:border-vexu/50'}`} data-id="element-2340" data-mp="32u4kknmtgsnb">
                  <div className="flex items-start gap-3.5" data-id="element-2341" data-mp="s1jkhpdmq01e">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2342" data-mp="s003txo29mz3">
                      <Phone className="w-5 h-5" data-id="element-2343" data-mp="2j7pg8i1fg1b4" />
                    </div>
                    <div data-id="element-2344" data-mp="2x4ujka9ram2o">
                      <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2345" data-mp="2x39xza5f6dgy">
                        Mobile number
                      </p>
                      <p className={`text-xs leading-relaxed mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2346" data-mp="20f3rsisund0k">
                        Set up the answering rules you want, such as when busy
                        or unanswered.
                      </p>
                    </div>
                  </div>
                </button>

                <button onClick={() => setSelection('landline')} className={`w-full p-4 rounded-2xl text-left border transition-colors ${isLight ? 'bg-white border-slate-200 hover:border-vexu/50 hover:bg-vexu/[0.02] shadow-sm' : 'bg-white/[0.04] border-white/10 hover:border-vexu/50'}`} data-id="element-2347" data-mp="1lo4jz9zu8si1">
                  <div className="flex items-start gap-3.5" data-id="element-2348" data-mp="xn16ebml0h79">
                    <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isLight ? 'bg-slate-100 text-slate-600' : 'bg-white/10 text-zinc-300'}`} data-id="element-2349" data-mp="xkogncal12jn">
                      <Building2 className="w-5 h-5" data-id="element-2350" data-mp="1va00celw3b1g" />
                    </div>
                    <div data-id="element-2351" data-mp="1v7x6tklj2zrz">
                      <p className={`text-sm font-bold ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2352" data-mp="1gdintk1yrqdb">
                        Landline number
                      </p>
                      <p className={`text-xs leading-relaxed mt-1 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2353" data-mp="4wslwd4kpkpi">
                        Forward all calls from your landline with one simple
                        code.
                      </p>
                    </div>
                  </div>
                </button>
              </div>

              <p className={`text-[11px] leading-relaxed text-center mt-6 px-4 ${isLight ? 'text-slate-400' : 'text-zinc-500'}`} data-id="element-2354" data-mp="3dcuc09xugn4s">
                You can manage or change your forwarding setup later.
              </p>
            </motion.div> : <motion.div key="landline-guide" initial={{
          opacity: 0,
          x: 18
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -18
        }} transition={{
          duration: 0.22
        }} className="pt-5" data-id="element-2355" data-mp="4xamnm0anov0">
              <button onClick={() => setSelection(null)} className={`text-sm font-semibold mb-7 ${isLight ? 'text-slate-500 hover:text-slate-800' : 'text-zinc-400 hover:text-white'}`} data-id="element-2356" data-mp="nuk5b0xsoyb7">
                ← Choose a different number
              </button>

              <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-5 ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2357" data-mp="2fm7gq98m2o0q">
                <Building2 className="w-7 h-7" data-id="element-2358" data-mp="2pi0qjkuy43et" />
              </div>
              <h2 className={`text-2xl font-bold tracking-tight mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2359" data-mp="2ugbz7s9i87vg">
                Forward your landline
              </h2>
              <p className={`text-sm leading-relaxed mb-6 ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2360" data-mp="338dw5gkd760z">
                Use the handset connected to the landline you’d like Vexu to
                answer.
              </p>

              <div className="space-y-3 mb-6" data-id="element-2361" data-mp="19wikyi6x9zln">
                {['Pick up your landline handset.', 'Dial *72, then enter your Vexu number.', 'Wait for your provider’s confirmation tone or message.'].map((step, index) => <div key={step} className={`flex items-start gap-3 p-3.5 rounded-2xl ${isLight ? 'bg-white border border-slate-200 shadow-sm' : 'bg-white/[0.04] border border-white/10'}`} data-id="element-2362" data-mp="1nnf0b7bu8kar" data-mpi={index}>
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 text-[11px] font-bold ${isLight ? 'bg-vexu/10 text-vexu-700' : 'bg-vexu/15 text-vexu'}`} data-id="element-2363" data-mp="1xk9w3igq86lc">
                      {index + 1}
                    </span>
                    <p className={`text-sm leading-relaxed pt-0.5 ${isLight ? 'text-slate-700' : 'text-zinc-300'}`} data-id="element-2364" data-mp="88hl5fj6zvgi">
                      {step}
                    </p>
                  </div>)}
              </div>

              <div className={`rounded-2xl p-4 mb-4 ${isLight ? 'bg-vexu/5 border border-vexu/20' : 'bg-vexu/10 border border-vexu/20'}`} data-id="element-2365" data-mp="3w3etiabyartm">
                <p className={`text-[11px] font-bold uppercase tracking-wider mb-2 ${isLight ? 'text-vexu-700' : 'text-vexu'}`} data-id="element-2366" data-mp="3n38gsqo2tvvi">
                  Dial from your landline
                </p>
                <div className="flex items-center justify-between gap-3" data-id="element-2367" data-mp="ioicmocl3yax">
                  <code className={`text-base font-bold tracking-wide ${isLight ? 'text-slate-900' : 'text-white'}`} data-id="element-2368" data-mp="emwwmu4r73wi">
                    *72 {VEXU_NUMBER}
                  </code>
                  <button onClick={copyForwardingNumber} aria-label="Copy Vexu number" className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${copied ? 'bg-vexu text-white' : isLight ? 'bg-white text-slate-500 border border-slate-200' : 'bg-white/10 text-zinc-300'}`} data-id="element-2369" data-mp="2zdnvw28d4ojl">
                    {copied ? <CheckCircle2 className="w-4 h-4" data-id="element-2370" data-mp="2mtqmwyxkb0op" /> : <Copy className="w-4 h-4" data-id="element-2371" data-mp="2qvu8igdy3lnp" />}
                  </button>
                </div>
              </div>

              <div className={`rounded-2xl px-4 py-3.5 mb-7 ${isLight ? 'bg-slate-100 border border-slate-200' : 'bg-white/[0.04] border border-white/10'}`} data-id="element-2372" data-mp="2d3e6niws5tod">
                <p className={`text-xs font-bold mb-1 ${isLight ? 'text-slate-700' : 'text-zinc-200'}`} data-id="element-2373" data-mp="23844mv6dtmal">
                  To turn it off later
                </p>
                <p className={`text-xs leading-relaxed ${isLight ? 'text-slate-500' : 'text-zinc-400'}`} data-id="element-2374" data-mp="1u7fpty95zhwn">
                  Dial <code className="font-bold" data-id="element-2375" data-mp="1fjea834dcz5p">*73</code> from the same
                  landline to deactivate call forwarding.
                </p>
              </div>

              <Button fullWidth onClick={onLandlineActivated} data-id="element-2376" data-mp="1am5m7lwji0wj">
                I’ve activated forwarding
              </Button>
            </motion.div>}
        </AnimatePresence>
      </div>

      {!isLandline && selection === 'mobile' && <div className="px-6 pb-8" data-id="element-2377" data-mp="1yhqfhds5tow0">
          <Button fullWidth onClick={onChooseMobile} data-id="element-2378" data-mp="1ygp7ji6fxiii">
            Continue with mobile
          </Button>
        </div>}
    </div>;
}